"use client";
import { getLocale, Locale } from "@ctv/shared-core/src";
import { createContext, PropsWithChildren, useContext, useState } from "react";
import { getFlightStaticData } from "./apis";
import { StaticData } from "./types";

type FlightStaticContextType = {
  data: StaticData;
  isLoading?: boolean;
  isError: boolean;
  refetch: () => void;
};

const FlightStaticContext = createContext<FlightStaticContextType>({
  data: undefined!,
  isLoading: false,
  isError: false,
  refetch: () => {
    throw new Error("FlightStaticContext not initialized");
  },
});

FlightStaticContext.displayName = "FlightStaticContext";

export default function FlightStaticProvider({
  data: initialData,
  isError: initialError = false,
  children,
}: PropsWithChildren<
  Omit<FlightStaticContextType, "setData" | "refetch" | "isLoading">
>) {
  const locale = getLocale();
  const [data, setData] = useState<StaticData>(initialData);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(initialError);

  function refetch() {
    setIsLoading(true);
    getFlightStaticData(locale || Locale.ENID).then((response) => {
      const { data, error } = response;
      setData(data);
      setIsError(error);
      setIsLoading(false);
    });
  }

  return (
    <FlightStaticContext.Provider value={{ data, isLoading, isError, refetch }}>
      {children}
    </FlightStaticContext.Provider>
  );
}

export function useFlightStaticData() {
  return useContext(FlightStaticContext);
}
